import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ChatAttachment, PASTED_IMAGE_LABEL, formatAttachmentSize } from '../../../models/chat.models';

/** What the preview dialog needs to render one attachment at a readable size. */
export interface AttachmentPreviewData {
  attachment: ChatAttachment;
  /** Short-lived signed URL, or a browser-local object URL for an unsent draft. */
  previewUrl: string | null;
}

/**
 * Shows one chat image attachment large enough to read (Issue #1746).
 *
 * A thumbnail in the composer is too small to check that the right screenshot was attached, which
 * is the whole reason this dialog exists. It renders the image at up to the viewport size and gives
 * it a real accessible name built from the file name and pixel dimensions, so someone using a
 * screen reader learns which image they opened rather than hearing "image".
 *
 * The dialog only ever receives a URL it was handed; it never constructs a storage path of its own.
 */
@Component({
  selector: 'app-attachment-preview-dialog',
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: './attachment-preview-dialog.component.html',
  styleUrls: ['./attachment-preview-dialog.component.scss'],
})
export class AttachmentPreviewDialogComponent {
  dialogRef = inject<MatDialogRef<AttachmentPreviewDialogComponent>>(MatDialogRef);
  data = inject<AttachmentPreviewData>(MAT_DIALOG_DATA);

  /** File name, or the pasted-image label for an image that arrived through the clipboard. */
  get title(): string {
    return this.data.attachment.displayName || PASTED_IMAGE_LABEL;
  }

  /** Pixel dimensions and file size, shown under the title and used in the accessible name. */
  get details(): string {
    const { width, height, byteSize } = this.data.attachment;
    const parts: string[] = [];
    if (width && height) parts.push(`${width} × ${height} pixels`);
    const size = formatAttachmentSize(byteSize);
    if (size) parts.push(size);
    return parts.join(' · ');
  }

  /** Accessible name for the image itself, so it is not announced as an unlabelled graphic. */
  get imageAlt(): string {
    return this.details ? `${this.title}, ${this.details}` : this.title;
  }

  /**
   * Whether this attachment is genuinely gone, as opposed to merely not loaded right now.
   *
   * <p>The distinction matters more than it looks. The dialog used to decide purely on whether it
   * had been handed a URL, so an intact image whose signed URL had simply not been fetched was
   * shown "This image is no longer available" — a claim of data loss about a file sitting safely in
   * storage. A blank frame reads as a rendering glitch and costs a user nothing; being told their
   * file is gone invites them to re-upload it, abandon the feature, or stop trusting it to hold
   * anything. Only the attachment's own state can say whether it is really gone, so only that is
   * allowed to produce the permanent message.
   *
   * <p>The template asks this before it asks whether a URL is in hand, and that order is the whole
   * fix rather than an implementation detail. A rejected draft still carries the browser-local blob
   * it was previewed from, so a URL-first test rendered an image the user cannot view and offered
   * no explanation at all — the exact mirror of the restored-image case above. The dialog was
   * silent where it owed an explanation and explaining where it owed silence, both because it was
   * reading the wrong thing. Asking the state first answers both.
   */
  get isUnavailable(): boolean {
    return this.data.attachment.state === 'failed' || this.data.attachment.state === 'expired';
  }

  /**
   * What to say about an attachment that is genuinely unavailable.
   *
   * <p>The backend's own reason is used whenever there is one, because it is more specific than
   * anything written here. It can legitimately be absent, though: a rejected draft stores
   * `validated.failureReason` unfiltered, and the default that exists alongside it is applied to
   * the spoken announcement rather than to the stored field.
   *
   * <p>The two states need different sentences, which is the whole reason this is not one string
   * with a fallback. "No longer available" describes something that was attached and has since
   * expired. Said about a file the server refused, it claims a loss that never happened - the same
   * false claim this dialog was just fixed to stop making, arriving through the other branch. The
   * strip beside it already draws this distinction; the dialog now matches its wording.
   */
  get unavailableMessage(): string {
    const reason = this.data.attachment.failureReason;
    if (reason) {
      return reason;
    }
    return this.data.attachment.state === 'expired'
      ? 'This image is no longer available.'
      : 'This image could not be attached.';
  }

  close(): void {
    this.dialogRef.close();
  }
}
